/**
 * Shop orders (shop-indents) — same APIs as Flutter Citizen + Mitra.
 * Coupon burns only when Mitra confirms payment (not on Redeem tap).
 */
import { createApiClient, getApiBaseUrl } from './httpClient';

const apiClient = createApiClient(getApiBaseUrl());

const normalizePhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

/** Encode UPI query values (spaces as %20); strip chars UPI apps dislike. */
const upiQueryValue = (raw, max = 80) => {
  const cleaned = String(raw || '')
    .replace(/[^\w\s.\-@&]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return '';
  const clipped = cleaned.length > max ? cleaned.slice(0, max) : cleaned;
  return encodeURIComponent(clipped);
};

/** Match Flutter ShopIndentsApi.buildUpiUri — do not encode @ in pa. */
const buildUpiUri = ({
  upiId,
  payeeName = 'Loksy Shop',
  amount = 0,
  tokenNo,
  indentId,
} = {}) => {
  const pa = String(upiId || '').trim();
  if (!pa) return '';
  const pn = upiQueryValue(payeeName || 'Loksy Shop', 50);
  const tnParts = [
    tokenNo && String(tokenNo).trim() ? `Token ${String(tokenNo).trim()}` : '',
    indentId && String(indentId).trim() ? String(indentId).trim() : '',
  ].filter(Boolean);
  const tn = upiQueryValue(tnParts.join(' '), 80);
  const am = Number(amount || 0).toFixed(2);
  return `upi://pay?pa=${pa}&pn=${pn}&am=${am}&cu=INR${tn ? `&tn=${tn}` : ''}`;
};

const buildClickablePayQrUrl = (upiPayUri, size = 280) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    upiPayUri,
  )}`;

const payAppLabel = (row = {}) => {
  switch (String(row.customerPayApp || row.customer_pay_app || 'any').toLowerCase()) {
    case 'phonepe':
      return 'PhonePe';
    case 'gpay':
      return 'Google Pay';
    case 'paytm':
      return 'Paytm';
    default:
      return 'PhonePe / GPay';
  }
};

const payRequestWhatsAppDigits = (row = {}) => {
  const upi = String(row.customerUpi || row.customer_upi || '').trim();
  const phone = String(row.customerPhone || row.customer_phone || '').trim();
  let raw = upi || phone;
  if (raw.includes('@')) raw = raw.split('@')[0];
  return normalizePhone(raw);
};

export const shopIndentsApi = {
  buildUpiUri,
  buildClickablePayQrUrl,
  payAppLabel,
  payRequestWhatsAppDigits,
  create: async (payload) => {
    const { data } = await apiClient.post('/shop-indents', payload);
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to send order');
    }
    return data.data;
  },

  mine: async () => {
    const { data } = await apiClient.get('/shop-indents/mine');
    if (!data?.success) return [];
    return Array.isArray(data.data) ? data.data : [];
  },

  update: async (id, payload) => {
    // Citizen edit path — Mitra actions stay on PATCH /shop-indents/:id
    const { data } = await apiClient.patch(`/shop-indents/${id}/edit`, payload);
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to update order');
    }
    return data.data;
  },

  resendAlert: async (id) => {
    const { data } = await apiClient.post(`/shop-indents/${id}/resend-alert`, {});
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to resend');
    }
    return data.data;
  },

  customerRespond: async (id, action, paymentNote) => {
    const { data } = await apiClient.patch(
      `/shop-indents/${id}/customer-response`,
      {
        action,
        ...(paymentNote ? { paymentNote } : {}),
      },
    );
    if (!data?.success) {
      throw new Error(data?.message || 'Failed');
    }
    return data.data;
  },

  listBusiness: async ({ status, businessId } = {}) => {
    const params = {};
    if (status) params.status = status;
    if (businessId) params.businessId = businessId;
    const { data } = await apiClient.get('/shop-indents/business', { params });
    if (!data?.success) return [];
    return Array.isArray(data.data) ? data.data : [];
  },

  pendingCount: async (businessId) => {
    const params = businessId ? { businessId } : {};
    try {
      const { data } = await apiClient.get(
        '/shop-indents/business/pending-count',
        { params },
      );
      if (data?.success) return Number(data.count) || 0;
    } catch {
      /* fall through */
    }
    const rows = await shopIndentsApi.listBusiness({
      status: 'pending',
      businessId,
    });
    return rows.length;
  },

  mitraAction: async (id, action, body = {}) => {
    const { data } = await apiClient.patch(`/shop-indents/${id}`, {
      action,
      ...body,
    });
    if (!data?.success) {
      throw new Error(data?.message || 'Action failed');
    }
    // Attach soft warning (e.g. bill create failed) without breaking callers.
    const row = data.data || null;
    if (row && (data.warning || data.billCreateError)) {
      row._warning = data.warning || data.billCreateError;
      row._billCreateError = data.billCreateError || null;
    }
    return row;
  },

  getById: async (id) => {
    const { data } = await apiClient.get(`/shop-indents/${id}`);
    if (!data?.success) return null;
    return data.data || null;
  },

  publicPayUrl: (indentId) => {
    const base = getApiBaseUrl().replace(/\/+$/, '');
    return `${base}/shop-indents/public-pay/${encodeURIComponent(indentId)}`;
  },

  /** Same WhatsApp text as Flutter ShopIndentsApi.whatsappShareText */
  whatsappShareText: (row = {}) => {
    const token = String(row.tokenNo || '').trim();
    const amount =
      Number(row.finalAmountInr ?? row.subtotalInr ?? 0) || 0;
    const upi = String(row.upiId || '').trim();
    const name = String(row.customerName || 'Customer').trim();
    const shop = String(row.businessName || 'Shop').trim();
    const payApp = payAppLabel(row);
    const indentId = String(row.id || '').trim();
    const payHttps = indentId ? shopIndentsApi.publicPayUrl(indentId) : '';
    return [
      `*Loksy — tap to pay (${payApp})*`,
      ...(token ? [`Token: *${token}*`] : []),
      `Hi ${name}, *${shop}* approved your order.`,
      `Amount: *₹${amount.toFixed(2)}*`,
      '',
      ...(payHttps
        ? [
            `👉 *Tap this link to pay* (opens ${payApp}):`,
            payHttps,
            '',
          ]
        : []),
      ...(upi ? [`Shop UPI (backup): \`${upi}\``] : []),
      '',
      'You can pay from home — no need to be at the shop.',
      'After paying, send screenshot to the shop. Thank you!',
    ].join('\n');
  },

  /** Deep-link intents for counter / mobile (same as public-pay HTML). */
  paymentAppLinks: (upiUri) => {
    if (!upiUri) return { gpay: '', phonePe: '', any: '' };
    return {
      gpay: upiUri.replace('upi://pay?', 'tez://upi/pay?'),
      phonePe: upiUri.replace('upi://pay?', 'phonepe://pay?'),
      any: upiUri,
    };
  },

  resolveShop: async ({ businessUserId, shopId, shopName } = {}) => {
    const candidates = [businessUserId, shopId]
      .map((v) => String(v || '').trim())
      .filter(Boolean);

    for (const id of candidates) {
      try {
        const { data } = await apiClient.get(
          `/users/businesses/public/${encodeURIComponent(id)}`,
        );
        const row = data?.data || data?.business || data;
        if (row && (row.id || row.user_id || row.business_id)) {
          return {
            id: String(row.id || row.user_id || row.business_id),
            name: String(
              row.business_name ||
                row.name ||
                row.shop_name ||
                shopName ||
                'Shop',
            ),
          };
        }
      } catch {
        /* try next */
      }
    }

    const q = String(shopName || '').trim();
    if (!q || q.toLowerCase() === 'shop' || q.toLowerCase() === 'locsy') {
      return null;
    }
    try {
      const { data } = await apiClient.get('/users/businesses/by-category', {
        params: { search: q, limit: 20 },
      });
      const list = data?.data || data?.businesses || data || [];
      const arr = Array.isArray(list) ? list : [];
      const lower = q.toLowerCase();
      const hit =
        arr.find(
          (b) =>
            String(b.business_name || b.name || '')
              .toLowerCase()
              .includes(lower),
        ) || arr[0];
      if (!hit) return null;
      return {
        id: String(hit.id || hit.user_id || hit.business_id),
        name: String(hit.business_name || hit.name || q),
      };
    } catch {
      return null;
    }
  },

  normalizePhone,
};

export default shopIndentsApi;
